import { useState, useEffect, useMemo } from "react";
import { sqlRead, clean } from "../utils/sqlClient";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

const COMMON_PARTIES = ["CleanSpace", "Engineer of Record", "GC", "Owner", "Vendor", "By Others"];

const EMPTY_FORM = {
  ItemID: null,
  ItemName: "",
  Discipline: "",
  GuidanceNotes: "",
  VendorCategory: "",
  ResponsibilityDesignBuild: "",
  ResponsibilityPlanSpec: "",
  SortOrder: "",
  IsActive: true,
};

export default function BODItemLibrary() {
  const isAdmin = useIsAdmin();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(null);   // null = no editor open; object = editing/creating
  const [search, setSearch]     = useState("");
  const [filterDiscipline, setFilterDiscipline] = useState("All");
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    setLoading(true);
    sqlRead("EXEC sp_GetBODItems")
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const disciplines = useMemo(
    () => ["All", ...new Set(items.map(i => i.Discipline))].filter(Boolean),
    [items]
  );

  // Party dropdown options: common list plus anything already used as a default
  const partyOptions = useMemo(() => {
    const set = new Set(COMMON_PARTIES);
    items.forEach(i => {
      if (i.ResponsibilityDesignBuild) set.add(i.ResponsibilityDesignBuild);
      if (i.ResponsibilityPlanSpec)    set.add(i.ResponsibilityPlanSpec);
    });
    return Array.from(set);
  }, [items]);

  const filtered = items.filter(i => {
    if (!showInactive && !i.IsActive) return false;
    if (filterDiscipline !== "All" && i.Discipline !== filterDiscipline) return false;
    if (search && !i.ItemName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = {};
  filtered.forEach(i => {
    const d = i.Discipline || "(No Discipline)";
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(i);
  });

  const handleSave = async () => {
    if (!form?.ItemName?.trim()) return;
    setSaving(true);
    try {
      await sqlRead(`EXEC sp_SaveBODItem
        ${form.ItemID ? `@item_id=${form.ItemID},` : ""}
        @item_name='${clean(form.ItemName)}',
        @discipline=${form.Discipline ? `'${clean(form.Discipline)}'` : "NULL"},
        @guidance_notes=${form.GuidanceNotes ? `'${clean(form.GuidanceNotes)}'` : "NULL"},
        @vendor_category=${form.VendorCategory ? `'${clean(form.VendorCategory)}'` : "NULL"},
        @resp_design_build=${form.ResponsibilityDesignBuild ? `'${clean(form.ResponsibilityDesignBuild)}'` : "NULL"},
        @resp_plan_spec=${form.ResponsibilityPlanSpec ? `'${clean(form.ResponsibilityPlanSpec)}'` : "NULL"},
        @sort_order=${form.SortOrder !== "" && form.SortOrder !== null ? +form.SortOrder : "NULL"},
        @is_active=${form.IsActive ? 1 : 0}`);
      setForm(null);
      load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    await sqlRead(`EXEC sp_SaveBODItem
      @item_id=${item.ItemID},
      @item_name='${clean(item.ItemName)}',
      @discipline=${item.Discipline ? `'${clean(item.Discipline)}'` : "NULL"},
      @guidance_notes=${item.GuidanceNotes ? `'${clean(item.GuidanceNotes)}'` : "NULL"},
      @vendor_category=${item.VendorCategory ? `'${clean(item.VendorCategory)}'` : "NULL"},
      @resp_design_build=${item.ResponsibilityDesignBuild ? `'${clean(item.ResponsibilityDesignBuild)}'` : "NULL"},
      @resp_plan_spec=${item.ResponsibilityPlanSpec ? `'${clean(item.ResponsibilityPlanSpec)}'` : "NULL"},
      @sort_order=${item.SortOrder !== null && item.SortOrder !== undefined ? item.SortOrder : "NULL"},
      @is_active=${item.IsActive ? 0 : 1}`).catch(console.error);
    load();
  };

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="panel-title">BOD Item Library</div>
          <div className="panel-sub">
            The master checklist every project draws from. Set default responsibility per delivery method here —
            projects seed from these defaults, then override per item without touching this library.
          </div>
        </div>
        <button
          onClick={() => setForm({ ...EMPTY_FORM })}
          style={{ height: 32, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--cs-navy)", border: "none", borderRadius: "var(--radius)", cursor: "pointer", flexShrink: 0 }}
        >
          + New Item
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items…"
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", outline: "none", minWidth: 160 }}
        />
        <select value={filterDiscipline} onChange={e => setFilterDiscipline(e.target.value)}
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, background: "#fff", outline: "none" }}>
          {disciplines.map(d => <option key={d}>{d}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text2)", cursor: "pointer" }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: "auto" }}>
          {filtered.length} item{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Editor */}
      {form && (
        <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: 16, marginBottom: 18, background: "#FAFBFC" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {form.ItemID ? "Edit Item" : "New Item"}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Field label="Item name *">
              <input value={form.ItemName} onChange={e => setForm(f => ({ ...f, ItemName: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="Discipline">
              <input value={form.Discipline} onChange={e => setForm(f => ({ ...f, Discipline: e.target.value }))} list="discipline-list" style={inputStyle} />
              <datalist id="discipline-list">
                {disciplines.filter(d => d !== "All").map(d => <option key={d} value={d} />)}
              </datalist>
            </Field>
            <Field label="Vendor category">
              <input value={form.VendorCategory} onChange={e => setForm(f => ({ ...f, VendorCategory: e.target.value }))} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: 10, marginBottom: 10 }}>
            <Field label="Responsibility — Design-Build default">
              <input value={form.ResponsibilityDesignBuild} onChange={e => setForm(f => ({ ...f, ResponsibilityDesignBuild: e.target.value }))} list="party-list" style={inputStyle} />
            </Field>
            <Field label="Responsibility — Plan-and-Spec default">
              <input value={form.ResponsibilityPlanSpec} onChange={e => setForm(f => ({ ...f, ResponsibilityPlanSpec: e.target.value }))} list="party-list" style={inputStyle} />
            </Field>
            <Field label="Sort order">
              <input type="number" value={form.SortOrder ?? ""} onChange={e => setForm(f => ({ ...f, SortOrder: e.target.value }))} style={inputStyle} />
            </Field>
            <datalist id="party-list">
              {partyOptions.map(p => <option key={p} value={p} />)}
            </datalist>
          </div>

          <Field label="Guidance notes">
            <textarea
              value={form.GuidanceNotes}
              onChange={e => setForm(f => ({ ...f, GuidanceNotes: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, height: "auto", padding: "6px 9px", resize: "vertical", fontFamily: "inherit" }}
            />
          </Field>

          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button
              onClick={handleSave}
              disabled={saving || !form.ItemName?.trim()}
              style={{ height: 30, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: form.ItemName?.trim() ? "var(--cs-navy)" : "#cbd5e1", border: "none", borderRadius: "var(--radius)", cursor: form.ItemName?.trim() ? "pointer" : "default" }}
            >
              {saving ? "Saving…" : "Save item"}
            </button>
            <button onClick={() => setForm(null)} style={{ height: 30, padding: "0 12px", fontSize: 12, color: "var(--text2)", background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Item list */}
      {loading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading library…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          No items match your filters.
        </div>
      ) : (
        Object.entries(grouped).map(([discipline, dItems]) => (
          <div key={discipline} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {discipline}
              </div>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
              <div style={{ fontSize: 11, color: "var(--text3)" }}>{dItems.length}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {dItems.map(item => (
                <div key={item.ItemID} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                  padding: "8px 12px", background: item.IsActive ? "#fff" : "#fafafa",
                  opacity: item.IsActive ? 1 : 0.55,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: "var(--text)" }}>{item.ItemName}</span>
                    {item.VendorCategory && (
                      <span style={{ fontSize: 10, marginLeft: 6, color: "var(--text3)", padding: "1px 6px", borderRadius: 100, background: "#f3f4f6" }}>
                        {item.VendorCategory}
                      </span>
                    )}
                  </div>

                  <RespPair label="D-B" value={item.ResponsibilityDesignBuild} />
                  <RespPair label="P-S" value={item.ResponsibilityPlanSpec} />

                  <button onClick={() => setForm({ ...EMPTY_FORM, ...item })}
                    style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                    Edit
                  </button>
                  <button onClick={() => handleToggleActive(item)}
                    title={item.IsActive ? "Deactivate — hides from future checklists" : "Reactivate"}
                    style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                    {item.IsActive ? "Deactivate" : "Reactivate"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function RespPair({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, minWidth: 120 }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text3)", letterSpacing: "0.05em" }}>{label}</span>
      <span style={{
        fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
        background: value ? "#dbeafe" : "#fff",
        color: value ? "#1e40af" : "var(--text3)",
        outline: value ? "none" : "1px dashed var(--cs-border)",
      }}>
        {value || "—"}
      </span>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 30,
  border: "1px solid var(--cs-border)",
  borderRadius: "var(--radius)",
  padding: "0 9px",
  fontSize: 12,
  color: "var(--text)",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};
